import { TUploadedDocumentsData } from "../store/onboarding/types";

export interface IQualificationsMissingDocsInput {
  qualification?: string | null;
  currently_studying_option?: string | null;
  acecqa_approved?: boolean | null;
  alternative_positions?: string | string[] | null;
}

/**
 * Computes the list of required document types that are missing or empty.
 *
 * Single source of truth used by:
 *   - Qualifications (onboarding step)
 *   - MyProfile
 *   - ActivationAlert
 *   - OnlineInterview
 *   - OnboardingSteps (sidebar nav)
 */
export function computeMissingDocs(
  qualifications: IQualificationsMissingDocsInput | null | undefined,
  workingRights: string | null | undefined,
  documents: Pick<TUploadedDocumentsData, "document_type" | "file_path">[] | null | undefined
): string[] {
  const qualification = qualifications?.qualification ?? "";
  const currentlyStudyingOption = qualifications?.currently_studying_option ?? "";
  const acecqaApproved = qualifications?.acecqa_approved ?? false;
  const altPositions = qualifications?.alternative_positions ?? "";

  const isChefCook =
    !!altPositions &&
    altPositions !== "" &&
    (Array.isArray(altPositions)
      ? altPositions.includes("Chef/Cook")
      : (altPositions as string).includes("Chef/Cook"));

  const educatorQualifications = [
    "post_graduate_diploma",
    "bachelor_or_degree",
    "certificate_iii",
    "diploma",
  ];
  const requiredDocs: { condition: boolean; type: string }[] = [
    { condition: true, type: "working_with_children_check" },
    { condition: true, type: "mandatory_reporting_training" },
    { condition: !!acecqaApproved, type: "ACECQA_assessment" },
    { condition: isChefCook, type: "food_handler_certificate" },
    { condition: isChefCook, type: "cooking_qualification" },
    {
      condition:
        qualification !== "no_qualifications" &&
        (educatorQualifications.includes(qualification) ||
          (qualification === "currently_studying" &&
            ["diploma_equivalent", "certificate_iii_equivalent"].includes(currentlyStudyingOption))),
      type: "first_aid_certificate",
    },
    { condition: qualification.includes("post_graduate_diploma"), type: "post_graduate_diploma" },
    { condition: qualification.includes("bachelor_or_degree"), type: "bachelor_or_degree" },
    {
      condition: ["post_graduate_diploma", "bachelor_or_degree"].includes(qualification),
      type: "teachers_registration",
    },
    { condition: qualification === "certificate_iii", type: "certificate_iii" },
    { condition: qualification === "diploma", type: "diploma" },
    {
      condition: qualification === "currently_studying",
      type: "course_progression_letter",
    },
    { condition: workingRights === "australian_permanent_resident", type: "permanent_residency_visa" },
    { condition: workingRights === "australian_visa_holder", type: "australian_work_visa" },
  ];

  const missing: string[] = [];

  for (const { condition, type } of requiredDocs) {
    const doc = documents?.find((d) => d.document_type === type);
    if (condition && (!doc || doc.file_path === "")) {
      missing.push(type);
    }
  }

  return missing;
}

const FIRST_AID_OPTIONAL_POSITIONS = ["Admin Assistant", "Cleaner", "Gardener", "Chef/Cook"];

/** Decodes a position column (array, JSON or double-encoded JSON). Null/empty => [], unparseable => null. */
const decodePositions = (value: unknown): unknown[] | null => {
  if (value === null || value === undefined || value === "") return [];
  try {
    for (let i = 0; i < 2 && typeof value === "string"; i++) value = JSON.parse(value);
  } catch {
    return null;
  }
  return Array.isArray(value) ? value : null;
};

/**
 * Mirrors backend FirstAidHelper::isOptional. First Aid is optional only when there is no ECE
 * qualification and every selected position is a non-educator role. Fails closed (required).
 */
export function isFirstAidOptional(
  q:
    | {
        qualification?: string | null;
        currently_studying_option?: string | null;
        educator_teacher_positions?: unknown;
        alternative_positions?: unknown;
      }
    | null
    | undefined
): boolean {
  if (!q) return false;
  const qualification = q.qualification ?? "";
  if (["post_graduate_diploma", "bachelor_or_degree", "diploma", "certificate_iii"].includes(qualification)) return false;
  if (
    qualification === "currently_studying" &&
    ["diploma_equivalent", "certificate_iii_equivalent"].includes(q.currently_studying_option ?? "")
  )
    return false;

  const educator = decodePositions(q.educator_teacher_positions);
  const alternative = decodePositions(q.alternative_positions);
  if (educator === null || alternative === null) return false;

  const positions = [...educator, ...alternative];
  return positions.length > 0 && positions.every((p) => typeof p === "string" && FIRST_AID_OPTIONAL_POSITIONS.includes(p));
}
